<script>
  import { createEventDispatcher, onMount, tick } from 'svelte';

  export let height = null;
  export let lockAspectRatio = true;
  export let scale = 1;
  export let selected = false;
  export let width = null;

  export let objectPosition = 'center';

  const dispatch = createEventDispatcher();

  let resizing = false;
  let startX, startY, startWidth, startHeight, startRatio;
  let originalWidth, originalHeight;
  let element;

  onMount(async () => {
    await tick();
    if (element && scale !== 1) {
      originalWidth = element.offsetWidth;
      originalHeight = element.offsetHeight;
      width = originalWidth * scale;
      height = originalHeight * scale;
    }
  });

  let alignX = 'center';
  let alignY = 'center';

  $: {
    if (objectPosition === 'center') {
      alignX = 'center';
      alignY = 'center';
    } else {
      const parts = objectPosition.split(' ');
      // Default to center if parts are missing/malformed
      alignX = parts[0] || 'center';
      alignY = parts[1] || 'center';
    }
  }

  // If aligned left, handle goes on right. If aligned right, handle goes on left.
  // If centered, we default handle to the right/bottom for standard expansion.
  $: handleX = alignX === 'right' ? 'left' : 'right';
  $: handleY = alignY === 'bottom' ? 'top' : 'bottom';

  $: handleClass = `${handleY}-${handleX}`;

  function startResize(e) {
    e.preventDefault();
    e.stopPropagation();

    resizing = true;
    startX = e.clientX;
    startY = e.clientY;

    startWidth = element.offsetWidth;
    startHeight = element.offsetHeight;

    if (!originalWidth) {
      originalWidth = startWidth;
      originalHeight = startHeight;
    }

    startRatio = startWidth / startHeight;

    const cursorMap = {
      'top-left': 'nw-resize',
      'top-right': 'ne-resize',
      'bottom-left': 'sw-resize',
      'bottom-right': 'se-resize'
    };

    document.body.style.cursor = cursorMap[handleClass] || 'nwse-resize';
  }

  function onMouseMove(e) {
    if (!resizing) return;

    // Calculate raw movement
    const dx = (e.clientX - startX) / scale;
    const dy = (e.clientY - startY) / scale;

    // Setup directions and multipliers
    const invertX = handleX === 'left' ? -1 : 1;
    const invertY = handleY === 'top' ? -1 : 1;

    const multX = alignX === 'center' ? 2 : 1;
    const multY = alignY === 'center' ? 2 : 1;

    // What the size would be if we used X vs Y
    const candidateWidth = Math.max(20, startWidth + dx * invertX * multX);
    const candidateHeight = Math.max(20, startHeight + dy * invertY * multY);

    let newWidth, newHeight;
    const shouldLock = lockAspectRatio || e.shiftKey;

    if (shouldLock) {
      // Determine dominant axis by which dimension has changed more
      const widthChange = Math.abs(candidateWidth - startWidth);
      const heightChange = Math.abs(candidateHeight - startHeight);

      if (widthChange > heightChange * startRatio) {
        newWidth = candidateWidth;
        newHeight = newWidth / startRatio;
      } else {
        newHeight = candidateHeight;
        newWidth = newHeight * startRatio;
      }
    } else {
      // Free resize
      newWidth = candidateWidth;
      newHeight = candidateHeight;
    }

    width = newWidth;
    height = newHeight;

    dispatch('resize', { width, height });
  }

  function onMouseUp() {
    if (resizing) {
      resizing = false;
      document.body.style.cursor = '';
      dispatch('scale', { scale: width / originalWidth });
    }
  }
</script>

<svelte:window on:mousemove={onMouseMove} on:mouseup={onMouseUp} />

<!-- svelte-ignore a11y-no-noninteractive-element-interactions a11y-click-events-have-key-events -->
<div
  bind:this={element}
  class="resizable-wrapper"
  class:selected
  style:width={width ? `${width}px` : '100%'}
  style:height={height ? `${height}px` : '100%'}
  on:click
  role="group"
>
  <slot />

  {#if selected}
    <div class="border-overlay"></div>
    <div class="handle {handleClass}" on:mousedown={startResize} role="button" tabindex="-1"></div>
  {/if}
</div>

<style lang="scss">
  .resizable-wrapper {
    position: relative;
    display: inline-flex;

    :global(img),
    :global(canvas),
    :global(svg) {
      width: 100% !important;
      height: 100% !important;
      object-fit: fill;
    }
  }

  .border-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px dashed white;
    pointer-events: none;
    z-index: 10;
  }

  .handle {
    position: absolute;
    width: 24px;
    height: 24px;
    background: white;
    border: 1px solid black;
    border-radius: 50%;
    z-index: 11;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);

    &.top-left {
      top: -6px;
      left: -6px;
      cursor: nw-resize;
    }
    &.top-right {
      top: -6px;
      right: -6px;
      cursor: ne-resize;
    }
    &.bottom-left {
      bottom: -6px;
      left: -6px;
      cursor: sw-resize;
    }
    &.bottom-right {
      bottom: -6px;
      right: -6px;
      cursor: se-resize;
    }
  }
</style>
